import { useState } from "react";
import { Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";

import ModalComponent from "../../../shared/Ui/ModalComponent/ModalWindow";
import ModalButton from "../../../shared/Ui/Buttons/ModalButtons";
import CalendarSelectFormik from "../../../shared/Ui/Calendar/CalendarSelectFormik";
import SelectCustomFormik from "../../../shared/Ui/SelectCustom/SelectCustomFormik";
import style from "../../EventsAudit/EventAuditList.module.css";
import { useProductGroupOptions } from "../../../shared/hook/useProductGroupOptions";
import {
  ParticipantsRegistryReportFilter,
  createParticipantsRegistryReport,
} from "../../../common/api/participantsRegistryReportApi";
import { parseFormDate, toApiDate } from "./participantsRegistryUtils";
import { useOrganisationOptions } from "../useOrganisationOptions";

type FormValues = {
  productGroupIds: string[];
  organizationId: string;
  registrationDateFrom: string;
  registrationDateTo: string;
  contractDateFrom: string;
  contractDateTo: string;
};

const INITIAL_VALUES: FormValues = {
  productGroupIds: [],
  organizationId: "",
  registrationDateFrom: "",
  registrationDateTo: "",
  contractDateFrom: "",
  contractDateTo: "",
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  // id созданного отчёта из ответа POST (202)
  onCreated: (reportId?: number) => void;
};

const P = "participants_registry_report";

function ParticipantsRegistryCreateModal({ isOpen, onClose, onCreated }: Props) {
  const { t } = useTranslation();

  // Товарные группы с названием на языке интерфейса
  const { options: productGroupOptions } = useProductGroupOptions();
  const orgs = useOrganisationOptions(isOpen);
  const [formError, setFormError] = useState("");

  const validate = (values: FormValues) => {
    const ranges: [string, string][] = [
      [values.registrationDateFrom, values.registrationDateTo],
      [values.contractDateFrom, values.contractDateTo],
    ];
    for (const [fromValue, toValue] of ranges) {
      const from = parseFormDate(fromValue);
      const to = parseFormDate(toValue);
      if (from && to && from > to) return t("billing.periodInvalidRange");
    }
    return "";
  };

  const handleClose = () => {
    setFormError("");
    orgs.reset();
    onClose();
  };

  const handleSubmit = async (values: FormValues) => {
    setFormError("");
    const error = validate(values);
    if (error) {
      setFormError(error);
      return;
    }

    const organizationId = Number(values.organizationId);
    const payload: ParticipantsRegistryReportFilter = {
      productGroupIds: values.productGroupIds.length
        ? values.productGroupIds.map(Number)
        : undefined,
      organizationId:
        Number.isFinite(organizationId) && organizationId > 0
          ? organizationId
          : undefined,
      registrationDateFrom: toApiDate(values.registrationDateFrom),
      registrationDateTo: toApiDate(values.registrationDateTo),
      contractDateFrom: toApiDate(values.contractDateFrom),
      contractDateTo: toApiDate(values.contractDateTo),
    };

    try {
      const created = await createParticipantsRegistryReport(payload);
      handleClose();
      onCreated(created?.id);
    } catch (e: any) {
      console.error("Error creating participants registry report:", e);
      // 400 — по фильтрам нет данных, запись в журнале бэк не создаёт
      const fallback =
        e?.response?.status === 400
          ? t(`${P}.no_data_found`)
          : t("marking_codes_report.create_error");
      setFormError(
        e?.response?.data?.message || e?.response?.data?.errorMessage || fallback,
      );
    }
  };

  const renderDateRange = (
    label: string,
    fromName: keyof FormValues,
    toName: keyof FormValues,
  ) => (
    <div className={style.inputField}>
      <label className={style.inputLabel}>{label}</label>
      <div className="grid grid-cols-2 gap-3">
        <CalendarSelectFormik
          name={fromName}
          documentStatus="new"
          outputFormat="dd.MM.yyyy"
          showMonthYearSelect
          placeholder={t(`${P}.date_from_ph`)}
        />
        <CalendarSelectFormik
          name={toName}
          documentStatus="new"
          outputFormat="dd.MM.yyyy"
          showMonthYearSelect
          placeholder={t(`${P}.date_to_ph`)}
        />
      </div>
    </div>
  );

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={handleClose}
      headerContent={
        <h3 className="text-lg font-semibold">{t(`${P}.create_title`)}</h3>
      }
      customContent={
        <Formik initialValues={INITIAL_VALUES} onSubmit={handleSubmit}>
          {({ setFieldValue, isSubmitting }) => (
            <Form className="p-4 w-[520px] max-w-full flex flex-col gap-1">
              <div className={style.inputField}>
                <label className={style.inputLabel}>{t(`${P}.product_group`)}</label>
                <Field
                  component={SelectCustomFormik}
                  name="productGroupIds"
                  isMulti
                  isClearable
                  size="dm"
                  options={productGroupOptions}
                  placeholder={t(`${P}.all_product_groups`)}
                  onChange={(next: string[]) => setFieldValue("productGroupIds", next || [])}
                />
              </div>

              <div className={style.inputField}>
                <label className={style.inputLabel}>{t(`${P}.org_iin_bin`)}</label>
                <Field
                  component={SelectCustomFormik}
                  name="organizationId"
                  isClearable
                  isMulti={false}
                  size="dm"
                  options={orgs.iinOptions}
                  placeholder={t(`${P}.org_iin_bin_ph`)}
                  onInputChange={(value: string) => orgs.setIinQuery(value || "")}
                  noOptionsMessage={orgs.noOptionsMessage}
                  onChange={(next: string) => {
                    setFieldValue("organizationId", next || "");
                    orgs.selectOrg(next, orgs.iinOptions);
                  }}
                />
              </div>

              <div className={style.inputField}>
                <label className={style.inputLabel}>{t(`${P}.org_name`)}</label>
                <Field
                  component={SelectCustomFormik}
                  name="organizationId"
                  isClearable
                  isMulti={false}
                  size="dm"
                  options={orgs.nameOptions}
                  placeholder={t(`${P}.org_name_ph`)}
                  onInputChange={(value: string) => orgs.setNameQuery(value || "")}
                  noOptionsMessage={orgs.noOptionsMessage}
                  onChange={(next: string) => {
                    setFieldValue("organizationId", next || "");
                    orgs.selectOrg(next, orgs.nameOptions);
                  }}
                />
              </div>

              {renderDateRange(
                t(`${P}.registration_date`),
                "registrationDateFrom",
                "registrationDateTo",
              )}
              {renderDateRange(
                t(`${P}.contract_date`),
                "contractDateFrom",
                "contractDateTo",
              )}

              {formError && <p className="text-sm text-red-600 mt-2">{formError}</p>}

              <div className="flex justify-end gap-3 mt-4">
                <ModalButton type="button" onClick={handleClose}>
                  {t("billing.close")}
                </ModalButton>
                <ModalButton color="green" type="submit" disabled={isSubmitting}>
                  {t(`${P}.generate`)}
                </ModalButton>
              </div>
            </Form>
          )}
        </Formik>
      }
    />
  );
}

export default ParticipantsRegistryCreateModal;
